import { z } from 'zod'

import { numberOrStringToNumberSchema } from '@shared/schema/utils'

export const GetVmcTechnicalPathParameterSchema = z.object({
  siteId: numberOrStringToNumberSchema,
})

// Une VMC du synoptique : l'équipement macro ZONE_VMC, avec l'état consolidé de ses points enfants.
//
// `state` et `speed` sont nullables et valent toujours null aujourd'hui : les equipmentRef qui les
// porteront ne sont pas arrêtés. Ils figurent dès maintenant dans le contrat pour que le front soit
// écrit contre sa forme définitive — le jour où les refs existent, seule la lambda change.
export const VmcSchema = z.object({
  equipmentId: z.string(),
  name: z.string(),
  // Connectivité du macro-équipement (cf. resolveEquipmentConnectivity) : déconnecté, la tuile masque ses valeurs.
  disconnected: z.boolean(),
  lastSeenAt: z.string().nullable(),
  // Défaut consolidé des points DEFAULT_VMC enfants : true dès qu'un seul est actif. null quand aucun
  // point de défaut n'est collecté — un défaut inconnu n'est pas un équipement sain.
  fault: z.boolean().nullable(),
  faultLastTime: z.string().nullable(),
  // Marche / arrêt. En attente du ref dédié.
  state: z.boolean().nullable(),
  // Vitesse courante, libellé issu de l'énumération propre à l'équipement (PV/GV, V1/V2/V3…).
  // En attente du ref dédié et de son mapper.
  speed: z.string().nullable(),
})

export const VmcFloorSchema = z.object({
  id: z.number(),
  name: z.string(),
  isUnderground: z.boolean(),
  isRoof: z.boolean(),
  // Rang d'affichage, croissant du haut vers le bas. Redondant avec l'ordre du tableau, mais explicite :
  // le front le renvoie tel quel et n'a pas à le recalculer pour détecter un changement.
  position: z.number(),
  vmcs: VmcSchema.array(),
})

export const VmcBuildingSchema = z.object({
  id: z.number(),
  name: z.string(),
  floors: VmcFloorSchema.array(),
})

// `unassigned` porte les VMC du site rattachées à aucun étage : la colonne « VMC sans étage » de la page
// de configuration. Une VMC dont l'étage appartient à un bâtiment supprimé y retombe aussi, sans quoi
// elle deviendrait inatteignable.
export const GetVmcTechnicalResponseSchema = z.object({
  buildings: VmcBuildingSchema.array(),
  unassigned: VmcSchema.array(),
})

export type Vmc = z.infer<typeof VmcSchema>
export type VmcFloor = z.infer<typeof VmcFloorSchema>
export type VmcBuilding = z.infer<typeof VmcBuildingSchema>
export type GetVmcTechnicalPathParameter = z.infer<typeof GetVmcTechnicalPathParameterSchema>
export type GetVmcTechnicalResponse = z.infer<typeof GetVmcTechnicalResponseSchema>
